// Serviço para buscar dados de referência de alergia do banco de dados
import { supabase } from './supabase'

// Interfaces para os dados de referência
export interface AllergyCode {
  id: string
  code: string
  display: string
  category: 'food' | 'medication' | 'environment' | 'biologic'
  system: string
  description?: string
  is_active: boolean
}

export interface Manifestation {
  id: string
  code: string
  display: string
  category: string
  system: string
  description?: string
  severity_level: 'mild' | 'moderate' | 'severe'
  is_active: boolean
}

export interface ClinicalStatusCode {
  id: string
  code: string
  display: string
  system: string
  description?: string
  is_active: boolean
}

export interface VerificationStatusCode {
  id: string
  code: string
  display: string
  system: string
  description?: string
  is_active: boolean
}

// Classe de serviço para dados de referência de alergia
export class AllergyReferenceService {
  // Buscar todos os códigos de alergia
  static async getAllergyCodes(): Promise<{ data: AllergyCode[] | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('allergy_codes')
        .select('*')
        .eq('is_active', true)
        .order('display')

      if (error) {
        console.error('Erro ao buscar códigos de alergia:', error)
        return { data: null, error }
      }

      return { data: data || [], error: null }
    } catch (err) {
      console.error('Erro inesperado ao buscar códigos de alergia:', err)
      return { data: null, error: err }
    }
  }

  // Buscar códigos de alergia por categoria
  static async getAllergyCodesByCategory(category: string): Promise<{ data: AllergyCode[] | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('allergy_codes')
        .select('*')
        .eq('is_active', true)
        .eq('category', category)
        .order('display')

      if (error) {
        console.error(`Erro ao buscar códigos de alergia da categoria ${category}:`, error)
        return { data: null, error }
      }

      return { data: data || [], error: null }
    } catch (err) {
      console.error(`Erro inesperado ao buscar códigos de alergia da categoria ${category}:`, err)
      return { data: null, error: err }
    }
  }

  // Buscar todas as manifestações
  static async getManifestations(): Promise<{ data: Manifestation[] | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('manifestations')
        .select('*')
        .eq('is_active', true)
        .order('display')

      if (error) {
        console.error('Erro ao buscar manifestações:', error)
        return { data: null, error }
      }

      return { data: data || [], error: null }
    } catch (err) {
      console.error('Erro inesperado ao buscar manifestações:', err)
      return { data: null, error: err }
    }
  }

  // Buscar manifestações por categoria
  static async getManifestationsByCategory(category: string): Promise<{ data: Manifestation[] | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('manifestations')
        .select('*')
        .eq('is_active', true)
        .eq('category', category)
        .order('display')

      if (error) {
        console.error(`Erro ao buscar manifestações da categoria ${category}:`, error)
        return { data: null, error }
      }

      return { data: data || [], error: null }
    } catch (err) {
      console.error(`Erro inesperado ao buscar manifestações da categoria ${category}:`, err)
      return { data: null, error: err }
    }
  }

  // Buscar códigos de status clínico
  static async getClinicalStatusCodes(): Promise<{ data: ClinicalStatusCode[] | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('clinical_status_codes')
        .select('*')
        .eq('is_active', true)
        .order('display')

      if (error) {
        console.error('Erro ao buscar códigos de status clínico:', error)
        return { data: null, error }
      }

      return { data: data || [], error: null }
    } catch (err) {
      console.error('Erro inesperado ao buscar códigos de status clínico:', err)
      return { data: null, error: err }
    }
  }

  // Buscar códigos de status de verificação
  static async getVerificationStatusCodes(): Promise<{ data: VerificationStatusCode[] | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('verification_status_codes')
        .select('*')
        .eq('is_active', true)
        .order('display')

      if (error) {
        console.error('Erro ao buscar códigos de status de verificação:', error)
        return { data: null, error }
      }

      return { data: data || [], error: null }
    } catch (err) {
      console.error('Erro inesperado ao buscar códigos de status de verificação:', err)
      return { data: null, error: err }
    }
  }

  // Buscar código de alergia específico
  static async findAllergyCode(searchTerm: string): Promise<{ data: AllergyCode | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('allergy_codes')
        .select('*')
        .eq('is_active', true)
        .or(`display.ilike.%${searchTerm}%,code.eq.${searchTerm}`)
        .limit(1)
        .single()

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Erro ao buscar código de alergia específico:', error)
        return { data: null, error }
      }

      return { data: data || null, error: null }
    } catch (err) {
      console.error('Erro inesperado ao buscar código de alergia específico:', err)
      return { data: null, error: err }
    }
  }

  // Buscar manifestação específica
  static async findManifestation(searchTerm: string): Promise<{ data: Manifestation | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('manifestations')
        .select('*')
        .eq('is_active', true)
        .or(`display.ilike.%${searchTerm}%,code.eq.${searchTerm}`)
        .limit(1)
        .single()

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Erro ao buscar manifestação específica:', error)
        return { data: null, error }
      }

      return { data: data || null, error: null }
    } catch (err) {
      console.error('Erro inesperado ao buscar manifestação específica:', err)
      return { data: null, error: err }
    }
  }

  // Buscar dados completos para formulário (otimizado)
  static async getAllReferenceData(): Promise<{
    allergyCodes: AllergyCode[]
    manifestations: Manifestation[]
    clinicalStatusCodes: ClinicalStatusCode[]
    verificationStatusCodes: VerificationStatusCode[]
    error: any
  }> {
    try {
      const [allergyResult, manifestationResult, clinicalResult, verificationResult] = await Promise.all([
        this.getAllergyCodes(),
        this.getManifestations(),
        this.getClinicalStatusCodes(),
        this.getVerificationStatusCodes()
      ])

      // Verificar se houve algum erro
      const errors = [
        allergyResult.error,
        manifestationResult.error,
        clinicalResult.error,
        verificationResult.error
      ].filter(Boolean)

      if (errors.length > 0) {
        console.error('Erros ao buscar dados de referência:', errors)
        return {
          allergyCodes: [],
          manifestations: [],
          clinicalStatusCodes: [],
          verificationStatusCodes: [],
          error: errors[0] // Retorna o primeiro erro
        }
      }

      return {
        allergyCodes: allergyResult.data || [],
        manifestations: manifestationResult.data || [],
        clinicalStatusCodes: clinicalResult.data || [],
        verificationStatusCodes: verificationResult.data || [],
        error: null
      }
    } catch (err) {
      console.error('Erro inesperado ao buscar todos os dados de referência:', err)
      return {
        allergyCodes: [],
        manifestations: [],
        clinicalStatusCodes: [],
        verificationStatusCodes: [],
        error: err
      }
    }
  }
}

export default AllergyReferenceService